import { Component, h, Host, Prop, State,Event, EventEmitter, Listen, Method } from '@stencil/core';
import { fetchWithTimeout } from '@ticom/web-component-utils';
import { AutocompleteChangeEventDetail, AutocompleteOption, HTMLTiAutocompleteElement } from '@ticom/ui-components/dist';
import * as ListManagerInterfaces from '../add-to-myti-list/add-to-myti-list.interface';
import { default as languageStrings } from '../../global/l10n/AutocompleteMytiList.json';

@Component({
    tag: 'ti-autocomplete-for-myti-list',
    styleUrl: 'autocomplete-for-myti-list.scss',
    shadow: true
})
export class AutocompleteForMytiList {

    /**
     * Reference to input element inside ti-autocomplete
     */
    private _addPartsInputField: HTMLInputElement;

    /**
     * Reference to ti-autocomplete element
     */
    private _autocompleteElement: HTMLTiAutocompleteElement;

    /**
     * App settings value to have settings based on user
     */
    private _appSettings: ListManagerInterfaces.AppSettings = null;

    /**
     * Data for the current list
     */
    private _currentList: ListManagerInterfaces.ListInfo = null;

    /**
     * The intervalId to keep track of the debounce intervals
     */
    private _debounceIntervalId: any = null;

    /**
     * Reference to Error message
     */
    private _errorElement: HTMLSpanElement;

    /**
     * Data validity flag
     */
    private _isValid: boolean = false;

    /**
    * Reference to the max items allowed in list based on user
    */
    private _itemsLimit: number = null;

    /**
     * Locale-specific set of language strings
     */
    private _localeStrings: Record<string, string>;

    /**
     * Parts selected by user
     */
    private _parts: Set<string> = new Set<string>();

    /**
     * Div ref to show all tags added for list
     */
    private _partsDivRef: HTMLDivElement;

    /**
     * The previous value used for fetch so that it doesn't repeat back to back
     */
    private _prevFetchValue: string = '';

    /**
     * Constantly updating suggestion list based on what you type to show partial results
     */
    private _suggestionsList: AutocompleteOption[] = [];

    /**
     * Store user info data (if present) from prop during load, and
     * utilize it always so the we can have prop as non-mutable
     */
    private _userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Specifies progress state while data is loaded
     */
    @State() progressState: 'loading' | 'error' | 'ready' = 'loading';

    /**
     * Passed from parent component to uniquely identify events from this instance
     */
    @Prop() contextId: string = null;

    /**
    * Current data for the list being updated
    */
    @Prop() currentList: ListManagerInterfaces.ListInfo;

    /**
     * Specifies the delay of the debounce for parts info api call (in ms)
     */
    @Prop() debounceDurationMs: number = 500;

    /**
    * Base url for api calls
    */
    @Prop() domain: string;

    /**
     * Character limit for searching parts
     */
    @Prop() charLimit: number = ListManagerInterfaces.CHAR_LIMIT_FOR_SEARCH;

    /**
    * Maximum number of parts allowed in uploaded list
    */
    @Prop() itemsLimit: number = null;

    /**
     * List Id for current list
     * should be passed from details page/grid
     */
    @Prop() listId: number = null;

    /**
     * Specifies the locale
     */
    @Prop() locale: string = 'en-US';

    /**
     * Get the userInfo details if already available
     */
    @Prop() userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Event to be emitted in case of any error
     */
    @Event() myTiAutosuggestErrorEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerErrorEventDetail>;

    /**
     * Event to broadcast validity of selection
     */
    @Event() myTiBulkAddtoListEvent: EventEmitter<ListManagerInterfaces.MyTiBulkAddtoListEventDetail>;

    /**
     * component life cycle hooks
     */
    async componentWillLoad() {
        // load language strings for locale - fallback to 'en-US' or an empty object
        this._localeStrings = languageStrings[this.locale] || languageStrings['en-US'] || {};
        await this._initData();
    }

    /**
     * Event listener to remove parts when user click 'X'
     */
    @Listen('tiChipChange')
    onTiChipChange(event: CustomEvent<{ group: string; removed?: boolean; selected: boolean; type: string; value: any; }>) {
        if (event.detail.removed) {
            this._parts.delete(event.detail.value);
            const partsLimit = Math.min(this._itemsLimit - this._currentList.totalItemCount, ListManagerInterfaces.DEFAULT_ITEM_EDIT_LIMIT);
            this._autocompleteElement.disabled = (this._parts.size >= partsLimit);
        }
        if (this._parts.size === 0) {
            this._partsDivRef.hidden = true;
            this._isValid = false;
            this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, action: 'opn-search', isValid: this._isValid });
        }
    }

    /**
     * Method to get final selected data
     */
    @Method()
    async getItemsToAdd(): Promise<ListManagerInterfaces.ListItem[]> {
        const validatedData: ListManagerInterfaces.ListItem[] = [];
        this._parts.forEach((part: string) => {
            validatedData.push({
                itemId: null,
                listId: this.listId,
                partId: null,
                partNumber: part,
                type: null,
                customerReferenceNumber: null,
                quantity: 1,
                itemInfo: null
            });
        });
        return validatedData;
    }

    /**
     * Make api call to get the app settings
     */
    private async _getAppSettings(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/settings'
            : 'data/add-to-myti-list/app-settings.json';

        let errorResponseStatus: number = null;

        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting App Settings');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.AppSettings) => {
                this._appSettings = data;
            });
        } catch (err) {
            this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
        }

        if (!this._itemsLimit) {
            this._itemsLimit = this._appSettings ? this._getListItemsLimit() : ListManagerInterfaces.DEFAULT_LIST_ITEMS_LIMIT;
        }
    }

    /**
     * Make api call to get the current List for local dev testing. This can be removed later as currentlist is mandatory
     */
    private async _getCurrentListInfo(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + `/listmodel/api/v1/ext/secured/list/${this.listId}`
            : 'data/add-to-myti-list/edit-list.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error(`Error in getting List with id :: ${this.listId}`);
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo) => {
                this._currentList = data;
            });
        } catch (err) {
            this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
            this.progressState = 'error';
        }
    }

    /**
     * Method to get core content for search
     */
    private _getCoreContent() {
        return [
            <div class="ti-autocomplete-myti-container">
                <p>
                    {this._localeStrings['partNumberLimitWarning'].replace('{0}', this._currentList.totalItemCount.toString()).replace('{1}', this._itemsLimit.toString())}
                </p>
                <ti-autocomplete ref={el => this._autocompleteElement = el}
                    onTiAutocompleteChange={ev => this._onTiAutocompleteChange(ev)}>
                    <input ref={el => this._addPartsInputField = el}
                        slot="input"
                        type="input"
                        placeholder={this._localeStrings['searchPlaceholderText']}
                        onInput={ev => this._onSearchValueChange((ev.target as HTMLInputElement).value)}>
                    </input>
                    <span ref={el => this._errorElement = el} slot='no-results'>{this._localeStrings['noResultsText']}</span>
                </ti-autocomplete>
                <div hidden={true} class="ti-autocomplete-myti-list-parts" ref={(el) => this._partsDivRef = el}>
                    <p>{this._localeStrings['partsToBeAddedText']}</p>
                </div>
            </div>
        ];
    }

    /**
    * Return error content
    */
    private _getErrorContent() {
        return <p>{this._localeStrings['autocompleteErrorText']}</p>;
    }

    /**
     * Make api call for matching parts
     */
    private async _getPartInfo(value: string) {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/autocomplete/opn?q=' + value
            : 'data/autocomplete-mylist/parts.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Info');
                }
                return apiResponse.json();
            }).then((data: string[]) => {
                if (data) {
                    this._suggestionsList = data.map((result: string) => {
                        return { content: result, value: result };
                    });
                    this._autocompleteElement.setOptionsList(this._suggestionsList);
                }
            });
        } catch (err) {
            // don't set to error as a bad request due to unallowed characters shouldn't disable the whole component
            this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
        }
    }

    /**
     * Return the list limit from App Settings based on userType
     */
    private _getListItemsLimit(): number {
        if (this._userInfo.isCGX) {
            return this._appSettings.maxItemPerListCGX;
        } else {
            switch (this._userInfo.userType) {
                case 'TIER': return this._appSettings.maxItemPerListTIer;
                case 'DIRECT': return this._appSettings.maxItemPerListDirect;
                case 'MASSMARKET': return this._appSettings.maxItemPerListMassMarket;
                default: return ListManagerInterfaces.DEFAULT_LIST_LIMIT;
            }
        }
    }

    /**
     * Return the loading content
     */
    private _getLoadingContent() {
        return <ti-progress></ti-progress>;
    }

    /**
     * Make api call to get user info
     */
    private async _getUserInfo() {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/user'
            : 'data/add-to-myti-list/user-info.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Info');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.UserInfo) => {
                this._userInfo = data;
            });
        } catch (err) {
            this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
            this.progressState = 'error';
        }
    }

    /**
     * Method to initialize data for props
     */
    private async _initData() {
        this.progressState = 'loading';
        // initialize the props
        if (!this.userInfo) {
            await this._getUserInfo();
        } else {
            this._userInfo = this.userInfo;
        }

        let promise1: Promise<void>;
        let promise2: Promise<void>;

        if (!this.itemsLimit) {
            promise2 = this._getAppSettings();
        } else {
            this._itemsLimit = this.itemsLimit;
        }

        // If current list is not provided check for listId and fetch details
        if (!this.currentList && null != this.listId) {
            promise1 = this._getCurrentListInfo();
        } else if (null === this.currentList && null === this.listId) {
            const errorMsg: string = 'Error: Current list data not found';
            this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: null, errorMsg });
        } else {
            this._currentList = this.currentList;
        }

        await Promise.all([promise1, promise2]);
        this.progressState = 'ready';
    }

    /**
     * Generic method to make all api calls
     */
    private async _makeApiCall(apiUrl: string,
        methodType: ListManagerInterfaces.HttpMethodType,
        requestBody: any,
        timeout: number = ListManagerInterfaces.LIST_API_TIMEOUT_VALUE
    ): Promise<Response> {
        const apiResponse: Response = await fetchWithTimeout(apiUrl,
            {
                method: methodType,
                body: requestBody !== null && requestBody !== undefined ? JSON.stringify(requestBody) : null,
                credentials: 'include',
                headers: {
                    'Cache-Control': 'no-store, must-revalidate',
                    'Expires': '0',
                    'Content-Type': 'application/json'
                }
            }, timeout);

        return apiResponse;
    }

    /**
     * Method to fetch parts on debounce during input
     */
    private _onSearchValueChange(value: string) {
        this._errorElement.textContent = value?.length < this.charLimit ? this._localeStrings['charLimitErrorText'] : this._localeStrings['noResultsText'];
        if (value?.length < this.charLimit) {
            this._errorElement.textContent = this._localeStrings['charLimitErrorText'];

            // if min length constraint violated, don't fetch results and empty suggestions
            this._prevFetchValue = value;
            this._suggestionsList = [];
            clearInterval(this._debounceIntervalId);
            return;
        }

        // start interval for debounce
        const intervalId: NodeJS.Timeout = setInterval(() => {
            clearInterval(intervalId);
            if (intervalId === this._debounceIntervalId && this._prevFetchValue !== value) {
                this._debounceIntervalId = null;
                value = value.trim();
                if (value) {
                    this._prevFetchValue = value;
                    // make API call and populate results
                    this._getPartInfo(value);
                }
            }
        }, this.debounceDurationMs);
        this._debounceIntervalId = intervalId;
    }

    /**
     * Method to handle selections made on autocomplete element
     */
    private _onTiAutocompleteChange(event: CustomEvent<AutocompleteChangeEventDetail>) {
        const trigger: string = event.detail.trigger.toString();
        const value: string = event.detail.value;
        if (value && (trigger === 'listClick' || trigger === 'listEnter')) {
            if (!this._parts.has(value)) {
                this._parts.add(value);
                if (!this._isValid) {
                    this._isValid = true;
                    this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, action: 'opn-search', isValid: this._isValid });
                }
                this._partsDivRef.innerHTML += `<ti-chip value="${value}" removable></ti-chip>`;
            }
            const partsLimit: number = Math.min(this._itemsLimit - this._currentList.totalItemCount, ListManagerInterfaces.DEFAULT_ITEM_EDIT_LIMIT);
            this._autocompleteElement.disabled = (this._parts.size >= partsLimit);
            this._addPartsInputField.value = '';

            if (this._parts.size > 0) {
                this._partsDivRef.hidden = false;
            }
        }
    }

    render() {
        switch (this.progressState) {
            case 'loading': return <Host>{this._getLoadingContent()}</Host>;
            case 'ready': return <Host>{this._getCoreContent()}</Host>;
            case 'error': return <Host>{this._getErrorContent()}</Host>;
        }
    }
}
